/**
 * Camlin Companion — minimal chat transcript state (doc 116 CO1, talk-only).
 *
 * `CompanionWidget` renders its own lightweight text transcript (the SDK's
 * default chat UI is hidden — see `liveSession.ts`). This reducer is the
 * pure piece of that wiring so it stays unit-testable without a DOM.
 * `CompanionWidget` feeds it from two sources: the visitor's own typed
 * input (`user_sent`), and the `avatar:chat:content` window event the SDK
 * already dispatches for every bot reply (`assistant_reply`).
 */

export type CompanionChatRole = 'user' | 'assistant';

export interface CompanionChatMessage {
  id: string;
  role: CompanionChatRole;
  text: string;
  createdAt: string;
}

export type CompanionChatAction =
  | { type: 'user_sent'; text: string }
  | { type: 'assistant_reply'; text: string }
  | { type: 'reset' };

export interface CompanionChatReducerDeps {
  now?: () => string;
  makeId?: () => string;
}

let idCounter = 0;
function defaultMakeId(): string {
  idCounter += 1;
  return `companion-chat-${idCounter}-${Date.now()}`;
}

function defaultNow(): string {
  return new Date().toISOString();
}

export function companionChatReducer(
  state: CompanionChatMessage[],
  action: CompanionChatAction,
  deps: CompanionChatReducerDeps = {}
): CompanionChatMessage[] {
  const now = deps.now ?? defaultNow;
  const makeId = deps.makeId ?? defaultMakeId;

  switch (action.type) {
    case 'user_sent': {
      const text = action.text.trim();
      if (!text) {
        return state;
      }
      return [...state, { id: makeId(), role: 'user', text, createdAt: now() }];
    }
    case 'assistant_reply': {
      const text = action.text.trim();
      if (!text) {
        return state;
      }
      // Drop an assistant reply identical to the one already at the tail:
      // the visual channel replays the greeting when it reconnects (e.g.
      // mid model-load, or after the widget's own connection recovery), and
      // both live websockets can dispatch the same prompt. A visitor never
      // benefits from the same reply twice in a row; a genuinely repeated
      // answer always has the visitor's own message between the copies.
      const tail = state[state.length - 1];
      if (tail && tail.role === 'assistant' && tail.text === text) {
        return state;
      }
      return [...state, { id: makeId(), role: 'assistant', text, createdAt: now() }];
    }
    case 'reset':
      return [];
    default:
      return state;
  }
}

/** Shape of the `detail` payload on the SDK's `avatar:chat:content` CustomEvent. */
export interface AvatarChatContentEventDetail {
  text?: string;
  source?: 'bot' | 'user' | string;
}

/**
 * Narrows an `avatar:chat:content` event detail down to a chat action, or
 * `null` when it should be ignored (e.g. echoes of the visitor's own text,
 * which CompanionWidget already appended optimistically on send).
 */
export function chatActionFromAvatarChatContentEvent(
  detail: AvatarChatContentEventDetail | null | undefined
): CompanionChatAction | null {
  if (!detail || typeof detail.text !== 'string' || !detail.text.trim()) {
    return null;
  }
  if (detail.source && detail.source !== 'bot') {
    return null;
  }
  return { type: 'assistant_reply', text: detail.text };
}
